import { GetState, SetState } from 'types/zustand';
import { ActionRefreshScope } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions';
import {
  FolderForTree,
  FolderMessage,
  FolderPermission,
  FolderViewData,
  newFolderAddedMessage,
  newFolderChangedMessage,
  newFolderRemovedMessage,
  ParentFolderData,
  SubFolderViewData,
} from '@decisions/server/DecisionsFramework/ServiceLayer/Services/Folder';
import { ActionMessage } from '@decisions/server/DecisionsFramework/Utilities/Messaging/ActionMessage';
import { breakLease } from '@decisions/server/DecisionsFramework/ServiceLayer/Actions/Common/CommonActionService';
import { useFolderStore as FolderStore } from './FolderStore';
import { SystemFolderID } from './LeftPanel/FolderTree/FolderTreeUtils';
import {
  EntityActionInfo,
  isEntityActionInfo,
} from '@decisions/server/DecisionsFramework/Utilities/Messaging/EntityActionInfo';
import { ITreeNode } from 'components/Tree/CommonTree/CommonTreeNode';
import { IFolderTreeNode } from './LeftPanel/FolderTree/FolderTree';
import { DefaultNodeLoader } from './LeftPanel/FolderTree/NodeLoaders';
import { getFolderId, PortalStore, usePortalStore } from './PortalStore';
import { getLocationPartsFromUrl } from 'components/Router';

export interface IGetNotificationProps extends Omit<IRefreshFolderStorageProps, 'force'> {
  /** Root information about folder with 'folderId', if not exists folder will be removed from tree */
  folderViewData?: FolderViewData;
}

export interface IRefreshFolderStorageProps {
  folderId: string;
  /** Refresh parent Folders */
  refreshParents: boolean;
  /** Refresh children Folders */
  refreshChildren: boolean;
  /** Force folder refresh, even if folder do not shown in tree */
  force?: boolean;
}

export function createActionsHandlerStore(get: GetState<PortalStore>, set: SetState<PortalStore>) {
  // optimization to return Promise[] in cycle
  const refreshSelfScope = async (folder: string, actionMessage: ActionMessage) => {
    if (!shouldRefreshFolder(folder)) {
      return [];
    }

    const { getFolderViewData } = FolderStore.getState().folderStoreActions;
    const { refreshFolderStorage } = get().actions;
    const folderViewData = await getFolderViewData(folder);
    if (
      folderViewData?.SendEventsOnFolderSave ||
      folderViewData?.SendEventsOnFolderOwnedDataSave ||
      folderViewData?.SendEventsOnAddingToFolder
    ) {
      return [];
    }

    const force = actionMessage.ActionName === 'Manage Permissions';
    return refreshFolderStorage({ folderId: folder, refreshParents: false, refreshChildren: true, force });
  };

  return {
    onActionCompleted: async (actionMessage: ActionMessage) => {
      const { refreshFolderStorage } = get().actions;
      const { deleteFolderFromStorage } = FolderStore.getState().folderStoreActions;
      if (actionMessage.ActionTypeName === 'DecisionsFramework.ServiceLayer.Services.Folder.DeleteFolderAction') {
        deleteFolderFromStorage(actionMessage.FolderID);
      }
      if (actionMessage.RequiresLeaseCheck) {
        await breakLease({ actionUniqueName: actionMessage.ActionUniqueName });
      }

      const foldersToRefresh = getFoldersToRefresh(actionMessage.Entities);
      const eventsPromises = [] as Promise<FolderMessage[]>[];

      // Self and OwningFolder working in the same way
      if (actionMessage.RefreshScope === ActionRefreshScope.Self) {
        for (let folder of foldersToRefresh) {
          eventsPromises.push(refreshSelfScope(folder, actionMessage));
        }
      } else if (actionMessage.RefreshScope === ActionRefreshScope.OwningFolder) {
        for (let folder of foldersToRefresh) {
          eventsPromises.push(refreshFolderStorage({ folderId: folder, refreshParents: false, refreshChildren: true }));
        }
      } else if (actionMessage.RefreshScope === ActionRefreshScope.Tree) {
        eventsPromises.push(refreshRoot());
      } else if (actionMessage.RefreshScope === ActionRefreshScope.TreeAndOwningFolder) {
        eventsPromises.push(refreshRoot());
        for (let folder of foldersToRefresh) {
          eventsPromises.push(refreshFolderStorage({ folderId: folder, refreshParents: true, refreshChildren: true }));
        }
      }

      const events = await Promise.all(eventsPromises);
      sendNotifications(
        actionMessage,
        events.flatMap(i => i)
      );
    },
    refreshFolderStorage: async ({ folderId, refreshParents, refreshChildren, force }: IRefreshFolderStorageProps) => {
      const state = get();
      const { pageName } = getLocationPartsFromUrl();

      if (!shouldRefreshFolder(folderId) && !force) {
        return [];
      }

      const folderViewData = await FolderStore.getState().folderStoreActions.fetchAndUpdateFolderViewData(
        folderId,
        pageName
      );

      if (!state.isStudio && folderViewData?.FolderBehaviorType === 'DecisionsFramework.Design.DesignerProjectFolder') {
        return [];
      }

      return getNotifications({ folderId, refreshParents, refreshChildren, folderViewData });
    },
  };
}

function sendNotifications(actionMessage: ActionMessage, events: FolderMessage[]) {
  events.forEach(e => Decisions.ClientEventsHandler.SendNotification(e));
  // Sasha: not sure that we need navigate to folder here
}

function shouldRefreshFolder(folderId: string) {
  const state = usePortalStore.getState();
  const { hasNode } = state.actions;
  const currentFolderId = getFolderId(state);
  return currentFolderId === folderId || hasNode(folderId);
}

async function refreshRoot(): Promise<FolderMessage[]> {
  const notifications = [] as FolderMessage[];
  const rootFolders = (await new DefaultNodeLoader().getRootFolders()).filter(n => n.FolderID !== SystemFolderID);

  const portalStore = usePortalStore.getState();
  const impact = evaluateUpdateImpact(
    rootFolders,
    portalStore.folderTreeStores.get(portalStore.foldersPanelTitle)?.getStore()?.nodes ?? []
  );

  notifications.push(...impact.added.map(i => newFolderAddedMessage({ FolderId: i })));
  notifications.push(...impact.changed.map(i => newFolderChangedMessage({ FolderId: i })));
  notifications.push(...impact.removed.map(i => newFolderRemovedMessage({ FolderId: i })));

  return notifications;
}

function getNotifications({
  folderId,
  refreshParents,
  refreshChildren,
  folderViewData,
}: IGetNotificationProps): FolderMessage[] {
  const state = usePortalStore.getState();
  const treeNode = state.actions.findFolder(folderId);
  // if folderViewData do not exist, or folder will be hidden out (permission/behavior changed)
  if (
    !folderViewData ||
    (folderViewData?.CurrentUserPermissions & FolderPermission.CanView) !== FolderPermission.CanView
  ) {
    // remove
    if (!!treeNode) {
      return [newFolderRemovedMessage({ FolderId: folderId, ParentFolderId: treeNode?.data!.parent?.nodeId })];
    } else {
      return [];
    }
  }

  const notifications = [] as FolderMessage[];

  if (!treeNode && folderViewData.ShowInTree) {
    notifications.push(newFolderAddedMessage({ FolderId: folderId }));
  }

  if (refreshChildren && treeNode?.childNodes?.[0]?.nodeId !== '.SearchFolder') {
    const impact = evaluateUpdateImpact(folderViewData.SubFolders, treeNode?.childNodes ?? []);
    notifications.push(...impact.added.map(i => newFolderAddedMessage({ FolderId: i, ParentFolderId: folderId })));
    notifications.push(...impact.changed.map(i => newFolderChangedMessage({ FolderId: i })));
    notifications.push(...impact.removed.map(i => newFolderRemovedMessage({ FolderId: i, ParentFolderId: folderId })));
  }

  const treeNodeParent = treeNode?.data!.parent;
  const isSimulatedRootFolder = treeNode?.data!.folderNode.IsSimulatedRootFolder && !treeNodeParent;
  if (refreshParents && !isSimulatedRootFolder && treeNodeParent?.nodeId !== folderViewData.Parents[0]?.FolderId) {
    notifications.push(
      newFolderRemovedMessage({ FolderId: folderViewData.FolderID, ParentFolderId: treeNodeParent?.nodeId })
    );
    notifications.push(
      newFolderAddedMessage({ FolderId: folderViewData.FolderID, ParentFolderId: folderViewData?.Parents[0]?.FolderId })
    );
  }

  notifications.push(
    newFolderChangedMessage({
      FolderId: folderId,
      FolderViewData: folderViewData,
      // we have already processed FolderViewData in FolderStore when fetching it.
      // We don't need to process it again when this event goes out.
      IsProcessed: true,
    })
  );

  return notifications;
}

function evaluateUpdateImpact(newArray: SubFolderViewData[] | FolderForTree[], oldArray: ITreeNode<IFolderTreeNode>[]) {
  const added = [] as string[];
  const changed = [] as string[];

  newArray.forEach((newItem: SubFolderViewData | FolderForTree) => {
    const oldItem = oldArray.find(
      (o: ITreeNode<IFolderTreeNode>) => o.data!.folderNode.FolderID === getFolderIdFromObject(newItem)
    );
    if (!oldItem) {
      added.push(getFolderIdFromObject(newItem));
    } else {
      if (!isSameFolder(newItem, oldItem)) {
        changed.push(getFolderIdFromObject(newItem));
      }
    }
  });

  const removed = oldArray
    .filter(oldItem => !newArray.some((newItem: IFolderObjectUnion) => areFolderIdsEqual(newItem, oldItem)))
    .map(getFolderIdFromObject);

  return {
    removed,
    added,
    changed,
  };
}

type IFolderObjectUnion =
  | SubFolderViewData
  | ParentFolderData
  | FolderForTree
  | ITreeNode<IFolderTreeNode>
  | EntityActionInfo;

function getFolderIdFromObject(folder: IFolderObjectUnion) {
  if (isEntityActionInfo(folder as EntityActionInfo)) {
    // For these actions, the FolderId is the currently navigated/active folder, the EntityID is the subject of the action
    return (folder as EntityActionInfo).EntityID ?? '';
  }
  return (
    (folder as SubFolderViewData).FolderId ??
    (folder as ParentFolderData).FolderId ??
    (folder as FolderForTree).FolderID ??
    (folder as ITreeNode<IFolderTreeNode>).data?.folderNode?.FolderID ??
    ''
  );
}

/**
 * Compare all passed folderIds
 * @param args all objects whose folderIds should be equal
 * @returns true if all of the provided arguments' folderIds are equal
 */
function areFolderIdsEqual(object1: IFolderObjectUnion, object2: IFolderObjectUnion) {
  return getFolderIdFromObject(object1) === getFolderIdFromObject(object2);
}

function isSameFolder(newFolder: SubFolderViewData | FolderForTree, oldFolder: ITreeNode<IFolderTreeNode>) {
  const folderName =
    (newFolder as SubFolderViewData).FolderDisplayName ?? (newFolder as FolderForTree).FolderName ?? '';
  if (getFolderIdFromObject(newFolder) !== oldFolder.nodeId) {
    return false;
  } else if (folderName !== oldFolder.data!.folderNode.FolderName) {
    return false;
  } else if (!!newFolder.HasSubFolders !== !!oldFolder.hasChildren) {
    return false;
  }
  return true;
}

function getFoldersToRefresh(entities: EntityActionInfo[]): string[] {
  const result: string[] = [];

  for (var entity of entities) {
    if (entity.EntityClassName === 'DecisionsFramework.ServiceLayer.Services.Folder.Folder') {
      result.indexOf(decodeURIComponent(entity.EntityID)) === -1 && result.push(decodeURIComponent(entity.EntityID));
    }
    if (entity.FolderID?.length) {
      result.indexOf(decodeURIComponent(entity.FolderID)) === -1 && result.push(decodeURIComponent(entity.FolderID));
    }
  }

  return result;
}
